import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { projects } from '../../data/constants';
import {
  Section, Wrapper,
  Header, HeaderLeft, Label, Title, TotalCount,
  Divider, Row, Num, RowTitle, TagsCell, Tag, Arrow, Preview,
} from './ProjectsStyle';

gsap.registerPlugin(ScrollTrigger);

const Projects = ({ setOpenModal }) => {
  const sectionRef = useRef(null);
  const previewRef = useRef(null);
  const moveX      = useRef(null);
  const moveY      = useRef(null);

  /* ── Mouse-tracking preview ─────────────────────────────────────────── */
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;

    moveX.current = gsap.quickTo(el, 'x', { duration: 0.45, ease: 'power3.out' });
    moveY.current = gsap.quickTo(el, 'y', { duration: 0.45, ease: 'power3.out' });

    const onMove = (e) => {
      moveX.current(e.clientX + 24);
      moveY.current(e.clientY - 90);
    };

    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const showPreview = (imgSrc) => {
    const el = previewRef.current;
    if (!el) return;
    el.querySelector('img').src = imgSrc;
    el.classList.add('show');
  };

  const hidePreview = () => {
    previewRef.current?.classList.remove('show');
  };

  /* ── Entrance animations ─────────────────────────────────────────────── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.proj-header',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 85%' } }
      );

      gsap.fromTo('.proj-divider-top',
        { scaleX: 0 },
        { scaleX: 1, duration: 0.6, ease: 'power3.out', transformOrigin: 'left',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' } }
      );

      const rows = sectionRef.current?.querySelectorAll('.proj-row');
      if (rows?.length) {
        gsap.fromTo(rows,
          { y: 24, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, stagger: 0.07, ease: 'power3.out',
            scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' } }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section id="projects" ref={sectionRef}>
      <Wrapper>

        <Header className="proj-header">
          <HeaderLeft>
            <Label>Work</Label>
            <Title>Projects</Title>
          </HeaderLeft>
          <TotalCount>{String(projects.length).padStart(2, '0')}</TotalCount>
        </Header>

        <Divider className="proj-divider-top" />

        {projects.map((project, i) => (
          <React.Fragment key={project.id}>
            <Row
              className="proj-row"
              onMouseEnter={() => project.image && showPreview(project.image)}
              onMouseLeave={hidePreview}
              onClick={() => setOpenModal({ state: true, project })}
            >
              <Num>{String(i + 1).padStart(2, '0')}</Num>
              <RowTitle>{project.title}</RowTitle>
              <TagsCell>
                {project.tags?.slice(0, 3).map((tag, ti) => (
                  <Tag key={ti}>{tag}</Tag>
                ))}
              </TagsCell>
              <Arrow>↗</Arrow>
            </Row>
            <Divider />
          </React.Fragment>
        ))}

      </Wrapper>

      {/* ── Floating image preview ──────────────────── */}
      <Preview ref={previewRef}>
        <img src="" alt="" />
      </Preview>
    </Section>
  );
};

export default Projects;
